import { useState, useEffect, useRef } from "react";
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
  getDoc,
  getDocs,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { db, storage } from "./firebase";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { fmt } from "./utils";

// ─── SAMPLE DATA (seeded on first run) ───────────────────────────────────────
const SAMPLE_CUSTOMERS = [
  { firstName: "Julie", lastName: "Fiset", company: "", email: "juliefiset40@gmail.com", phone: "418-558-9546", address: "1109 Rue de Châteauroux", city: "Québec", province: "Québec", postal: "G1G 1P3", type: "Residential", status: "Prospect", source: "Referral", notes: "Cousines Max Gamache, entrée d'eau 2026, terrassement 2027." },
  { firstName: "Mike", lastName: "Amyotte", company: "", email: "amyottemike@hotmail.com", phone: "", address: "", city: "Québec", province: "Québec", postal: "", type: "Residential", status: "Prospect", source: "None", notes: "" },
  { firstName: "Paule", lastName: "Turgeon", company: "", email: "paula797@outlook.com", phone: "581-980-1377", address: "", city: "Québec", province: "Québec", postal: "", type: "Residential", status: "Prospect", source: "None", notes: "" },
  { firstName: "Dave", lastName: "Murphy", company: "", email: "dave@murphy.com", phone: "555-123-1234", address: "", city: "Breakyville", province: "Quebec", postal: "", type: "Residential", status: "Prospect", source: "None", notes: "" },
];

const SAMPLE_LEADS = [
  { name: "Murphy Patio", customerId: "", value: 25000, stage: "site-visit", service: "Hardscape", date: "2026-03-16", notes: "" },
];

const SAMPLE_PRICE_LIST = [
  { name: "0-3/4\" Gravier", category: "Material", unit: "t.m.", cost: 12, price: 26, supplier: "BLC" },
  { name: "1/2\" Pierre Net", category: "Material", unit: "t.m.", cost: 17, price: 30, supplier: "BLC" },
  { name: "1/4\" Pierre Net", category: "Material", unit: "t.m.", cost: 19.25, price: 30, supplier: "BLC" },
  { name: "Arbres", category: "Material", unit: "pc", cost: 250, price: 350, supplier: "Centre Jardin Paradis" },
  { name: "Transport Machinerie", category: "Labor", unit: "hr", cost: 125, price: 175, supplier: "" },
  { name: "Opérateur Machinerie", category: "Labor", unit: "hr", cost: 44.5, price: 87.64, supplier: "" },
  { name: "Pelle 50G", category: "Equipment", unit: "hr", cost: 39.27, price: 52.36, supplier: "" },
  { name: "Asphalte", category: "Subcontractor", unit: "pi2", cost: 3.75, price: 4.69, supplier: "Pavage OB" },
];

const SAMPLE_ESTIMATES = [
  { number: "EST-414001", title: "Piscine Paule", customerId: "", serviceType: "Hardscape", status: "draft", validUntil: "", sections: [
    { id: "s1", title: "Excavation Piscine (121 t.m.)", items: [
      { id: "i1", name: "Transport Machinerie", category: "Labor", qty: 1, unitCost: 125, unitPrice: 175 },
      { id: "i2", name: "Pelle 50G", category: "Equipment", qty: 1, unitCost: 39.27, unitPrice: 52.36 },
    ]},
    { id: "s2", title: "Remblais Fond Piscine (20 t.m.)", items: [
      { id: "i3", name: "0-3/4\" Gravier", category: "Material", qty: 20, unitCost: 12, unitPrice: 26 },
    ]},
  ]},
  { number: "EST-288909", title: "Test Job", customerId: "", serviceType: "Hardscape", status: "draft", validUntil: "", sections: [] },
  { number: "EST-215613", title: "Murphy Patio", customerId: "", serviceType: "Hardscape", status: "draft", validUntil: "2026-03-17", sections: [] },
];

const SAMPLE_SETTINGS = {
  theme: "system", companyName: "Taylor Amyotte et Fils", phone: "(418) 561-2756",
  email: "info@tafexcavation.com", website: "www.tafexcavation.com",
  address: "20901 boul. Henri-Bourassa", city: "Quebec", province: "Québec", postal: "G2N 1P8",
  gst: "749062873 RT0001", qst: "1227231242 TQ0001", logo: null,
  terms: "Informations additionnelles\nModalités et Conditions de Paiement\nTaylor Amyotte et Fils Inc. exige un versement initial de 50% du montant total avant de débuter les travaux."
};

// ─── PORTAL HELPERS ───────────────────────────────────────────────────────────
function _genToken() {
  return Array.from(crypto.getRandomValues(new Uint8Array(20)))
    .map(b => b.toString(16).padStart(2, "0")).join("").slice(0, 32);
}

/** Ensures a portalToken exists on the customer doc, returns the portal URL. */
export async function generatePortalToken(customer) {
  let token = customer.portalToken;
  if (!token) {
    token = _genToken();
    await updateDoc(doc(db, "customers", customer.id), {
      portalToken: token,
      portalSentAt: new Date().toISOString(),
    });
  }
  return `https://portail.tafexcavation.com/portal/${token}`;
}

/** Generates token if needed, then sends the portal invite email. Returns { success, error }. */
export async function inviteCustomerToPortal(customer, settings) {
  try {
    let token = customer.portalToken;
    if (!token) {
      token = _genToken();
      await updateDoc(doc(db, "customers", customer.id), {
        portalToken: token,
        portalSentAt: new Date().toISOString(),
      });
      customer = { ...customer, portalToken: token };
    }
    await sendPortalEmail(customer, settings);
    return { success: true, error: null };
  } catch (err) {
    return { success: false, error: err.message || "Erreur" };
  }
}

// ─── PORTAL EMAIL ─────────────────────────────────────────────────────────────
export async function sendPortalEmail(customer, settings) {
  const portalUrl = `https://portail.tafexcavation.com/portal/${customer.portalToken}`;
  const companyName = settings?.companyName || "Taylor Amyotte et Fils";
  const companyPhone = settings?.phone || "";
  const companyEmail = settings?.email || "";
  const firstName = customer.firstName || "";

  const html = `<!DOCTYPE html>
<html lang="fr">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>Portail client</title></head>
<body style="margin:0;padding:0;background:#f5f5f5;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f5f5f5;padding:32px 0;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.08);">
        <!-- Header -->
        <tr><td style="background:#1a0a0a;padding:28px 40px;">
          <p style="margin:0;font-size:22px;font-weight:700;color:#ffffff;letter-spacing:0.5px;">${companyName}</p>
        </td></tr>
        <!-- Body -->
        <tr><td style="padding:40px 40px 32px;">
          <p style="margin:0 0 16px;font-size:16px;color:#1a1a1a;">Bonjour ${firstName},</p>
          <p style="margin:0 0 8px;font-size:15px;color:#333333;line-height:1.6;">
            Votre portail client est maintenant disponible. Vous pouvez y consulter vos soumissions, factures et nous envoyer des messages.
          </p>
          <p style="margin:0 0 32px;font-size:14px;color:#666666;line-height:1.6;">
            Your client portal is now available. You can view your estimates, invoices and send us messages.
          </p>
          <!-- Button -->
          <table cellpadding="0" cellspacing="0" style="margin:0 0 24px;">
            <tr><td style="border-radius:6px;background:#b91c1c;">
              <a href="${portalUrl}" target="_blank" style="display:inline-block;padding:14px 28px;font-size:15px;font-weight:600;color:#ffffff;text-decoration:none;border-radius:6px;">
                Accéder à mon portail&nbsp;/&nbsp;Access my portal
              </a>
            </td></tr>
          </table>
          <!-- Fallback URL -->
          <p style="margin:0 0 0;font-size:12px;color:#999999;word-break:break-all;">${portalUrl}</p>
        </td></tr>
        <!-- Footer -->
        <tr><td style="background:#f9f9f9;padding:20px 40px;border-top:1px solid #eeeeee;">
          <p style="margin:0;font-size:12px;color:#999999;line-height:1.8;">
            ${companyPhone ? companyPhone + "<br>" : ""}${companyEmail ? companyEmail : ""}
          </p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;

  const ref = await addDoc(collection(db, "mail"), {
    to: customer.email,
    message: {
      subject: `Votre portail client — ${companyName}`,
      html,
    },
    createdAt: serverTimestamp(),
    metadata: {
      type: "portal-invite",
      customerId: customer.id,
    },
  });
  return ref;
}

export async function sendPaymentReminder(customer, invoice, milestone, settings) {
  const portalUrl = `https://portail.tafexcavation.com/portal/${customer.portalToken}`;
  const companyName = settings?.companyName || "Taylor Amyotte et Fils";
  const companyPhone = settings?.phone || "";
  const companyEmail = settings?.email || "";
  const firstName = customer.firstName || "";
  const triggerLabels = {
    signing: "À la signature / At signing",
    milestone: "Mi-travaux / Mid-project",
    completion: "À la complétion / At completion",
    custom: "Date fixe / Fixed date",
  };
  const dueDisplay = milestone.dueDate || triggerLabels[milestone.dueTrigger] || "—";

  const html = `<!DOCTYPE html>
<html lang="fr">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>Rappel de paiement</title></head>
<body style="margin:0;padding:0;background:#f5f5f5;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f5f5f5;padding:32px 0;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.08);">
        <!-- Header -->
        <tr><td style="background:#1a0a0a;padding:28px 40px;">
          <p style="margin:0;font-size:22px;font-weight:700;color:#ffffff;letter-spacing:0.5px;">${companyName}</p>
        </td></tr>
        <!-- Body -->
        <tr><td style="padding:40px 40px 32px;">
          <p style="margin:0 0 16px;font-size:16px;color:#1a1a1a;">Bonjour ${firstName},</p>
          <p style="margin:0 0 8px;font-size:15px;color:#333333;line-height:1.6;">
            Ce message est un rappel pour le versement suivant associé à votre projet.
          </p>
          <p style="margin:0 0 24px;font-size:14px;color:#666666;line-height:1.6;">
            This is a reminder for the following payment associated with your project.
          </p>
          <!-- Payment details table -->
          <table width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 28px;border:1px solid #e5e3df;border-radius:6px;overflow:hidden;">
            <tr style="background:#f8f7f5;">
              <td style="padding:10px 16px;font-size:11px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:#a8a29e;border-bottom:1px solid #e5e3df;">Versement / Payment</td>
              <td style="padding:10px 16px;font-size:11px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:#a8a29e;border-bottom:1px solid #e5e3df;text-align:right;">Détails / Details</td>
            </tr>
            <tr style="border-bottom:1px solid #f3f4f6;">
              <td style="padding:10px 16px;font-size:13px;color:#57534e;">Versement / Payment</td>
              <td style="padding:10px 16px;font-size:13px;font-weight:600;color:#1c1917;text-align:right;">${milestone.label}</td>
            </tr>
            <tr style="border-bottom:1px solid #f3f4f6;">
              <td style="padding:10px 16px;font-size:13px;color:#57534e;">Montant / Amount</td>
              <td style="padding:10px 16px;font-size:13px;font-weight:700;color:#b91c1c;text-align:right;">${fmt(milestone.amount)}</td>
            </tr>
            <tr style="border-bottom:1px solid #f3f4f6;">
              <td style="padding:10px 16px;font-size:13px;color:#57534e;">Échéance / Due date</td>
              <td style="padding:10px 16px;font-size:13px;font-weight:600;color:#1c1917;text-align:right;">${dueDisplay}</td>
            </tr>
            <tr>
              <td style="padding:10px 16px;font-size:13px;color:#57534e;">Statut / Status</td>
              <td style="padding:10px 16px;font-size:13px;font-weight:600;color:#d97706;text-align:right;">En attente / Pending</td>
            </tr>
          </table>
          <!-- Button -->
          <table cellpadding="0" cellspacing="0" style="margin:0 0 24px;">
            <tr><td style="border-radius:6px;background:#b91c1c;">
              <a href="${portalUrl}" target="_blank" style="display:inline-block;padding:14px 28px;font-size:15px;font-weight:600;color:#ffffff;text-decoration:none;border-radius:6px;">
                Voir mon portail&nbsp;/&nbsp;View my portal
              </a>
            </td></tr>
          </table>
          <!-- Fallback URL -->
          <p style="margin:0;font-size:12px;color:#999999;word-break:break-all;">${portalUrl}</p>
        </td></tr>
        <!-- Footer -->
        <tr><td style="background:#f9f9f9;padding:20px 40px;border-top:1px solid #eeeeee;">
          <p style="margin:0;font-size:12px;color:#999999;line-height:1.8;">
            ${companyPhone ? companyPhone + "<br>" : ""}${companyEmail ? companyEmail : ""}
          </p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;

  const ref = await addDoc(collection(db, "mail"), {
    to: customer.email,
    message: {
      subject: `Rappel de paiement — ${companyName}`,
      html,
    },
    createdAt: serverTimestamp(),
    metadata: {
      type: "payment-reminder",
      invoiceId: invoice.id,
      milestoneId: milestone.id,
      customerId: customer.id,
    },
  });
  return ref;
}

// ─── SEND ESTIMATE EMAIL ──────────────────────────────────────────────────────
export async function sendEstimateEmail(customer, estimate, settings) {
  const portalUrl = `https://portail.tafexcavation.com/portal/${customer.portalToken}`;
  const companyName = settings?.companyName || "Taylor Amyotte et Fils";
  const companyPhone = settings?.phone || "";
  const companyEmail = settings?.email || "";
  const firstName = customer.firstName || "";
  const total = fmt((estimate.sections || []).reduce((sum, sec) => {
    if (sec.price != null) return sum + sec.price;
    return sum + (sec.items || []).reduce((s, it) => s + (it.unitPrice || 0) * (it.qty || 1), 0);
  }, 0) * 1.14975);

  const html = `<!DOCTYPE html>
<html lang="fr">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>Soumission</title></head>
<body style="margin:0;padding:0;background:#f5f5f5;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f5f5f5;padding:32px 0;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.08);">
        <!-- Header -->
        <tr><td style="background:#1a0a0a;padding:28px 40px;">
          <p style="margin:0;font-size:22px;font-weight:700;color:#ffffff;letter-spacing:0.5px;">${companyName}</p>
        </td></tr>
        <!-- Body -->
        <tr><td style="padding:40px 40px 32px;">
          <p style="margin:0 0 16px;font-size:16px;color:#1a1a1a;">Bonjour ${firstName},</p>
          <p style="margin:0 0 8px;font-size:15px;color:#333333;line-height:1.6;">
            Veuillez trouver ci-joint votre soumission <strong>${estimate.number || ""}</strong> d'un montant de <strong>${total}</strong>. Cliquez le bouton ci-dessous pour consulter et approuver votre soumission.
          </p>
          <p style="margin:0 0 32px;font-size:14px;color:#666666;line-height:1.6;">
            Please find your estimate <strong>${estimate.number || ""}</strong> for <strong>${total}</strong> below. Click the button to review and approve your estimate.
          </p>
          <!-- Button -->
          <table cellpadding="0" cellspacing="0" style="margin:0 0 24px;">
            <tr><td style="border-radius:6px;background:#b91c1c;">
              <a href="${portalUrl}" target="_blank" style="display:inline-block;padding:14px 28px;font-size:15px;font-weight:600;color:#ffffff;text-decoration:none;border-radius:6px;">
                Voir ma soumission&nbsp;/&nbsp;View my estimate
              </a>
            </td></tr>
          </table>
          <!-- Fallback URL -->
          <p style="margin:0 0 0;font-size:12px;color:#999999;word-break:break-all;">${portalUrl}</p>
        </td></tr>
        <!-- Footer -->
        <tr><td style="background:#f9f9f9;padding:20px 40px;border-top:1px solid #eeeeee;">
          <p style="margin:0;font-size:12px;color:#999999;line-height:1.8;">
            ${companyPhone ? companyPhone + "<br>" : ""}${companyEmail ? companyEmail : ""}
          </p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;

  const ref = await addDoc(collection(db, "mail"), {
    to: customer.email,
    message: {
      subject: `Soumission ${estimate.number || ""} — ${companyName}`,
      html,
    },
    metadata: {
      type: "estimate",
      estimateId: estimate.id,
      customerId: customer.id,
    },
    createdAt: serverTimestamp(),
  });
  return ref;
}

// ─── SEND THANK YOU EMAIL ─────────────────────────────────────────────────────
export async function sendThankYouEmail(customer, invoice, settings, lang) {
  try {
    const companyName = settings?.companyName || "Taylor Amyotte et Fils";
    const companyPhone = settings?.phone || "";
    const isFr = lang === "fr";
    const rawTemplate = isFr
      ? (settings?.thankYouEmailFR || "")
      : (settings?.thankYouEmailEN || "");
    const bodyText = rawTemplate
      ? rawTemplate.replace(/{clientName}/g, customer.firstName || "")
      : (isFr
          ? `Bonjour ${customer.firstName || ""},\n\nMerci pour votre confiance. Ce fut un plaisir de travailler avec vous.\n\n${companyName}`
          : `Hello ${customer.firstName || ""},\n\nThank you for choosing us. It was a pleasure working with you.\n\n${companyName}`);
    const invoiceNumber = invoice.number ? `#${invoice.number}` : "";
    const subject = isFr ? `Merci — ${invoiceNumber}` : `Thank you — ${invoiceNumber}`;
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/></head><body style="font-family:sans-serif;color:#222;max-width:600px;margin:0 auto;padding:24px">
<p style="font-size:15px;line-height:1.7;white-space:pre-wrap">${bodyText.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")}</p>
${companyPhone?`<p style="margin-top:20px;font-size:13px;color:#666">${companyName} · ${companyPhone}</p>`:`<p style="margin-top:20px;font-size:13px;color:#666">${companyName}</p>`}
</body></html>`;

    await addDoc(collection(db, "mail"), {
      to: customer.email,
      message: { subject, html },
      metadata: { type: "thank-you", invoiceId: invoice.id, customerId: customer.id },
      createdAt: serverTimestamp(),
    });

    await addDoc(collection(db, "communications"), {
      type: "thank-you",
      direction: "outbound",
      subject,
      body: bodyText,
      date: new Date().toISOString().split("T")[0],
      customerId: customer.id,
      invoiceId: invoice.id,
      isTask: false,
      taskDone: false,
      sentAt: new Date().toISOString(),
    });

    return { success: true, error: null };
  } catch (err) {
    return { success: false, error: err.message || "Erreur" };
  }
}

// ─── SEED DATABASE ON FIRST RUN ───────────────────────────────────────────────
export async function seedIfEmpty() {
  try {
    const settingsRef = doc(db, "meta", "seeded");
    const snap = await getDoc(settingsRef);
    if (snap.exists()) return; // already seeded

    // Seed all collections
    for (const c of SAMPLE_CUSTOMERS) await addDoc(collection(db, "customers"), c);
    for (const l of SAMPLE_LEADS)    await addDoc(collection(db, "leads"), l);
    for (const p of SAMPLE_PRICE_LIST) await addDoc(collection(db, "priceList"), p);
    for (const e of SAMPLE_ESTIMATES) await addDoc(collection(db, "estimates"), e);
    await setDoc(doc(db, "settings", "company"), SAMPLE_SETTINGS);
    await setDoc(settingsRef, { done: true }); // mark as seeded
    console.log("Database seeded with sample data.");
  } catch (err) {
    console.error("Seed error:", err);
  }
}

// ─── GENERIC COLLECTION HOOK ──────────────────────────────────────────────────
// Returns [items, addItem, updateItem, deleteItem]
export function useCollection(collectionName) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, collectionName), (snap) => {
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [collectionName]);

  const addItem = async (data) => {
    const { id, ...rest } = data;
    const ref = await addDoc(collection(db, collectionName), rest);
    return { id: ref.id, ...rest };
  };

  const updateItem = async (id, data) => {
    const { id: _id, ...rest } = data;
    await updateDoc(doc(db, collectionName, id), rest);
  };

  const deleteItem = async (id) => {
    await deleteDoc(doc(db, collectionName, id));
  };

  return [items, addItem, updateItem, deleteItem, loading];
}

// ─── COMMUNICATIONS HOOK ──────────────────────────────────────────────────────
export function useCommunications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "communications"), (snap) => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Sort by date desc, createdAt as tiebreaker
      docs.sort((a, b) => {
        const da = a.date || "";
        const db2 = b.date || "";
        if (db2 !== da) return db2.localeCompare(da);
        const ta = a.createdAt?.seconds || 0;
        const tb = b.createdAt?.seconds || 0;
        return tb - ta;
      });
      setItems(docs);
      setLoading(false);
    });
    return unsub;
  }, []);

  const addComm = async (data) => {
    const { id, ...rest } = data;
    const ref = await addDoc(collection(db, "communications"), {
      ...rest,
      createdAt: serverTimestamp(),
    });
    return { id: ref.id, ...rest };
  };

  const updateComm = async (id, data) => {
    const { id: _id, ...rest } = data;
    await updateDoc(doc(db, "communications", id), rest);
  };

  const deleteComm = async (id) => {
    await deleteDoc(doc(db, "communications", id));
  };

  return [items, addComm, updateComm, deleteComm, loading];
}

// ─── SETTINGS HOOK ────────────────────────────────────────────────────────────
export function useSettings() {
  const [settings, setSettingsState] = useState(SAMPLE_SETTINGS);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "settings", "company"), (snap) => {
      if (snap.exists()) setSettingsState(snap.data());
    });
    return unsub;
  }, []);

  const saveSettings = async (data) => {
    await setDoc(doc(db, "settings", "company"), data);
  };

  return [settings, saveSettings];
}

// ─── PERMISSIONS HOOK ─────────────────────────────────────────────────────────
export function usePermissions() {
  const [permissions, setPermissions] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "settings", "permissions"), (snap) => {
      if (snap.exists()) setPermissions(snap.data());
      else setPermissions(null);
    });
    return unsub;
  }, []);

  const savePermissions = async (data) => {
    await setDoc(doc(db, "settings", "permissions"), data);
  };

  return [permissions, savePermissions];
}

// ─── SCHEDULE NOTES HOOK ──────────────────────────────────────────────────────
export function useScheduleNotes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "scheduleNotes"), (snap) => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      docs.sort((a,b) => (a.startDate||"").localeCompare(b.startDate||""));
      setItems(docs);
      setLoading(false);
    });
    return unsub;
  }, []);

  const addNote = async (data) => {
    const { id, ...rest } = data;
    const ref = await addDoc(collection(db, "scheduleNotes"), {
      ...rest, createdAt: serverTimestamp(),
    });
    return { id: ref.id, ...rest };
  };

  const updateNote = async (id, data) => {
    const { id: _id, ...rest } = data;
    await updateDoc(doc(db, "scheduleNotes", id), rest);
  };

  const deleteNote = async (id) => {
    await deleteDoc(doc(db, "scheduleNotes", id));
  };

  return [items, addNote, updateNote, deleteNote, loading];
}// ─── PRODUCTION LOGS HOOK ─────────────────────────────────────────────────────
// Shift-summary entries (foreman/admin, once or twice per day per job)
// Usage: const [logs, addLog, , deleteLog] = useProductionLogs(jobId);
export function useProductionLogs(jobId) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!jobId) { setItems([]); setLoading(false); return; }
    const q = query(collection(db, "productionLogs"), where("jobId", "==", jobId));
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          // Sort by date desc, then enteredAt desc
          if (b.date !== a.date) return (b.date || "").localeCompare(a.date || "");
          return (b.enteredAt?.seconds || 0) - (a.enteredAt?.seconds || 0);
        });
      setItems(docs);
      setLoading(false);
    });
    return unsub;
  }, [jobId]);

  const addLog = async (data) => {
    const { id, ...rest } = data;
    const ref = await addDoc(collection(db, "productionLogs"), {
      ...rest,
      enteredAt: serverTimestamp(),
    });
    return { id: ref.id, ...rest };
  };

  const deleteLog = async (id) => {
    await deleteDoc(doc(db, "productionLogs", id));
  };

  return [items, addLog, null, deleteLog, loading];
}

// ─── PRODUCTION EVENTS HOOK ───────────────────────────────────────────────────
// Per-event tally entries (real-time tap logging — each truck load, delivery, etc.)
// Usage: const [events, addEvent, , deleteEvent] = useProductionEvents(jobId);
export function useProductionEvents(jobId) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!jobId) { setItems([]); setLoading(false); return; }
    const q = query(collection(db, "productionEvents"), where("jobId", "==", jobId));
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.loggedAt?.seconds || 0) - (a.loggedAt?.seconds || 0));
      setItems(docs);
      setLoading(false);
    });
    return unsub;
  }, [jobId]);

  const addEvent = async (data) => {
    const { id, ...rest } = data;
    const ref = await addDoc(collection(db, "productionEvents"), {
      ...rest,
      loggedAt: serverTimestamp(),
    });
    return { id: ref.id, ...rest };
  };

  const deleteEvent = async (id) => {
    await deleteDoc(doc(db, "productionEvents", id));
  };

  return [items, addEvent, null, deleteEvent, loading];
}

// ─── MAIL STATUS HOOK ─────────────────────────────────────────────────────────
export function useMailStatus(type, entityId) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!type || !entityId) return;
    const idField = {
      "portal-invite":    "metadata.customerId",
      "payment-reminder": "metadata.invoiceId",
      "estimate":         "metadata.estimateId",
    }[type];
    if (!idField) return;

    const q = query(
      collection(db, "mail"),
      where("metadata.type", "==", type),
      where(idField, "==", entityId),
      orderBy("createdAt", "desc"),
      limit(1)
    );

    getDocs(q).then(snap => {
      if (!snap.empty) {
        const delivery = snap.docs[0].data().delivery || {};
        setStatus({
          state:       delivery.state       || null,
          deliveredAt: delivery.deliveredAt || null,
          opened:      delivery.opened      || false,
          openedAt:    delivery.openedAt    || null,
          error:       delivery.error       || null,
        });
      }
    }).catch(err => console.error("useMailStatus error:", err));
  }, [type, entityId]);

  return status;
}

export function mailStatusBadge(status) {
  if (!status) return null;
  if (status.opened)                  return { label: "Ouvert",  icon: "👁",  color: "#16a34a" };
  if (status.state === "delivered")   return { label: "Livré",   icon: "✓",   color: "#2563eb" };
  if (status.state === "queued")      return { label: "En file", icon: "·",   color: "#ca8a04" };
  if (status.state === "bounced")     return { label: "Bounce",  icon: "⚠️",  color: "#dc2626" };
  if (status.state === "complained")  return { label: "Spam",    icon: "🚫",  color: "#dc2626" };
  return                                     { label: "Envoyé",  icon: "·",   color: "#6b7280" };
}

// ─── CREWS HOOK ───────────────────────────────────────────────────────────────
const DEFAULT_CREWS = [
  { name: "Équipe Rouge",   color: "#dc2626", memberIds: [] },
  { name: "Équipe Jaune",   color: "#ca8a04", memberIds: [] },
  { name: "Équipe Volante", color: "#2563eb", memberIds: [] },
];

export function useCrews() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const seeded = useRef(false);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "crews"), (snap) => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setItems(docs);
      setLoading(false);
      if (!seeded.current && snap.empty) {
        seeded.current = true;
        DEFAULT_CREWS.forEach(crew => addDoc(collection(db, "crews"), crew));
      }
    });
    return unsub;
  }, []);

  const addCrew = async (data) => {
    const { id, ...rest } = data;
    const ref = await addDoc(collection(db, "crews"), rest);
    return { id: ref.id, ...rest };
  };

  const updateCrew = async (id, data) => {
    const { id: _id, ...rest } = data;
    await updateDoc(doc(db, "crews", id), rest);
  };

  const deleteCrew = async (id) => {
    await deleteDoc(doc(db, "crews", id));
  };

  return [items, addCrew, updateCrew, deleteCrew, loading];
}

// ─── MATERIAL PHOTOS HOOK ─────────────────────────────────────────────────────
// Manages the materialPhotos collection (Settings library + Estimates reference)
export function useMaterialPhotos() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "materialPhotos"), (snap) => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setItems(docs);
    });
    return unsub;
  }, []);

  const addMaterialPhoto = async (file, name, caption, category) => {
    if (file.size > 10 * 1024 * 1024) throw new Error("Fichier trop volumineux (max 10 MB)");
    const timestamp = Date.now();
    const safeFilename = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const storagePath = `photos/materialLibrary/${timestamp}_${safeFilename}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);
    const photoURL = await getDownloadURL(storageRef);
    const docRef = await addDoc(collection(db, "materialPhotos"), {
      name: name || "",
      caption: caption || "",
      category: category || "Material",
      photoURL,
      storagePath,
      createdAt: serverTimestamp(),
    });
    return { id: docRef.id };
  };

  const deleteMaterialPhoto = async (item) => {
    try { await deleteObject(ref(storage, item.storagePath)); } catch (_) {}
    await deleteDoc(doc(db, "materialPhotos", item.id));
  };

  return [items, addMaterialPhoto, deleteMaterialPhoto];
}

export function MailStatusBadge({ status }) {
  const badge = mailStatusBadge(status);
  if (!badge) return null;
  return (
    <span style={{
      fontSize: 11,
      fontWeight: 500,
      color: badge.color,
      background: badge.color + "18",
      borderRadius: 4,
      padding: "2px 6px",
      marginLeft: 6,
      whiteSpace: "nowrap",
    }}>
      {badge.icon} {badge.label}
    </span>
  );
}
